import { getAddMembershipErrorType } from '@trello/business-logic/membership';
import { intl } from '@trello/i18n';

export const getAddMembershipErrorString = (
  error: string,
  user: string,
  workspaceMemberLimit?: string,
) => {
  const errorMessage = getAddMembershipErrorType(error);

  switch (errorMessage) {
    case 'restricted':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.restricted',
          defaultMessage:
            "{user} can't be added. They do not have an email domain that is allowed in this Workspace.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'must-reactivate':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.must-reactivate',
          defaultMessage:
            "{user} can't be added. They must reactivate their account.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'username-not-found':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.username-not-found',
          defaultMessage: "{user} can't be added. They were not found.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'not-in-enterprise':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.not-in-enterprise',
          defaultMessage:
            "{user} can't be added. They do not have SSO credentials for this Workspace.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'no-enterprise-licenses':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.no-enterprise-licenses',
          defaultMessage:
            "{user} can't be added. Your Enterprise has reached its member limit.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'rate-limit-exceeded':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.rate-limit-exceeded',
          defaultMessage:
            "{user} can't be added. You added too many people at once.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'existing':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.existing',
          defaultMessage:
            "{user} can't be added. They are already in this Workspace.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'too-many-members':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.too-many-members',
          defaultMessage:
            "{user} can't be added. This Workspace has reached the member limit ({workspaceMemberLimit}). To add new members, go to the Workspace setting page to remove some current members.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
          workspaceMemberLimit,
        },
      );

    case 'member-too-many-orgs':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.member-too-many-orgs',
          defaultMessage:
            "{user} can't be added because they are in the maximum number of Workspaces.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'deactivated-in-the-enterprise':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.deactivated-in-the-enterprise',
          defaultMessage:
            "{user} can't be added. They are deactivated in this Enterprise.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'not-managed-ent-member-or-valid-email':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.not-managed-ent-member-or-valid-email',
          defaultMessage:
            "{user} can't be added. They are not managed Enterprise members or do not have an allowed email domain.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'not-managed-ent-member':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.not-managed-ent-member',
          defaultMessage:
            "{user} can't be added. They are not managed Enterprise members.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'member-unconfirmed':
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.member-unconfirmed',
          defaultMessage:
            "{user} can't be added. To invite more members to this Workspace, you need to confirm your email address.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );

    case 'unauthorized-licensed-invite':
      return null;

    case 'unknown':
    default:
      return intl.formatMessage(
        {
          id: 'templates.workspace_join_requests.unknown',
          defaultMessage: "{user} can't be added. There was an unknown error.",
          description:
            'Error message when a user is restricted from joining a workspace',
        },
        {
          user,
        },
      );
  }
};
